import { useMemo, useState, useEffect } from 'react'
import {
  BarChart3, Building2, Package, TrendingUp, TrendingDown,
  Calendar, FileText, ChevronRight, Search, Info, Download, PlayCircle,
} from 'lucide-react'
import ReportiqTour from './ReportiqTour'

// ReportiQ dashboard — a customer-facing spend overview built on the same
// orders/order_lines data OrderiQ uses. Used inside PreviewLink when the
// group is in reporting-only mode, and also from the Reports tab in normal
// (ordering) mode as a richer alternative to the plain ReportsPane.

function fmtMoney(n) {
  return '£' + Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtMoneyShort(n) {
  const v = Number(n || 0)
  if (Math.abs(v) >= 1000) return '£' + (v / 1000).toFixed(1) + 'k'
  return '£' + v.toFixed(0)
}
function fmtQty(n) {
  const v = Number(n || 0)
  // Large numbers → whole units, no clutter of decimals.
  // Small numbers → up to 2 dp so 0.6 doesn't round to 1.
  if (Math.abs(v) >= 100) return Math.round(v).toLocaleString('en-GB')
  const rounded = Math.round(v * 100) / 100
  return Number.isInteger(rounded) ? rounded.toLocaleString('en-GB') : rounded.toLocaleString('en-GB', { maximumFractionDigits: 2 })
}
function fmtDate(s) {
  return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
function monthKey(d) {
  const dt = new Date(d)
  return dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0')
}
function monthLabel(key) {
  const [y, m] = key.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
}

// Date-range presets
const PRESETS = [
  { key: 'ytd',     label: 'Year to date' },
  { key: 'l12m',    label: 'Last 12 months' },
  { key: 'lq',      label: 'Last quarter' },
  { key: 'ly',      label: 'Previous year' },
  { key: 'all',     label: 'All time' },
  { key: 'custom',  label: 'Custom range' },
]

function computeRange(preset) {
  const now = new Date()
  const y = now.getFullYear()
  const iso = d => d.toISOString().slice(0, 10)
  switch (preset) {
    case 'ytd':  return { from: iso(new Date(y, 0, 1)), to: iso(now) }
    case 'l12m': return { from: iso(new Date(now.getTime() - 365 * 86400000)), to: iso(now) }
    case 'lq': {
      const q = Math.floor(now.getMonth() / 3)
      const lastQEnd = new Date(y, q * 3, 0)
      const lastQStart = new Date(y, q * 3 - 3, 1)
      return { from: iso(lastQStart), to: iso(lastQEnd) }
    }
    case 'ly':   return { from: iso(new Date(y - 1, 0, 1)), to: iso(new Date(y - 1, 11, 31)) }
    case 'all':  return { from: '2000-01-01', to: iso(now) }
    default:     return null
  }
}

export default function ReportiqDashboard({ orders = [], companies = [], group, brand, onApplyForOrdering, mode = 'reporting' }) {
  const [preset, setPreset] = useState('l12m')
  const initialRange = computeRange('l12m')
  const [from, setFrom] = useState(initialRange.from)
  const [to, setTo] = useState(initialRange.to)
  const [siteFilter, setSiteFilter] = useState('__all__')
  const [productFilter, setProductFilter] = useState('__all__')
  const [siteSearch, setSiteSearch] = useState('')
  const [tourOpen, setTourOpen] = useState(false)

  // Auto-open the tour on first landing per group — sticky in sessionStorage
  // so it doesn't nag on every page click. User can re-open with the button.
  const tourKey = `reportiq_tour_seen_${group?.id || 'unknown'}`
  useEffect(() => {
    try {
      if (!sessionStorage.getItem(tourKey)) {
        setTourOpen(true)
        sessionStorage.setItem(tourKey, '1')
      }
    } catch (_) { /* private mode: fine, just don't autolaunch */ }
  }, [tourKey])

  const applyPreset = (key) => {
    setPreset(key)
    const r = computeRange(key)
    if (r) { setFrom(r.from); setTo(r.to) }
  }

  // Site options
  const siteOptions = useMemo(() => {
    const map = new Map()
    for (const c of companies) map.set(c.id, c.name)
    for (const o of orders) {
      if (o.company_id && !map.has(o.company_id)) map.set(o.company_id, o.company_name || 'Unknown site')
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [companies, orders])

  const inRange = (d) => {
    if (!d) return false
    const iso = new Date(d).toISOString().slice(0, 10)
    return iso >= from && iso <= to
  }
  const inRangeLY = (d) => {
    if (!d) return false
    const iso = new Date(d).toISOString().slice(0, 10)
    const fromLY = new Date(new Date(from).getFullYear() - 1 + '-' + from.slice(5)).toISOString().slice(0, 10)
    const toLY   = new Date(new Date(to).getFullYear() - 1 + '-' + to.slice(5)).toISOString().slice(0, 10)
    return iso >= fromLY && iso <= toLY
  }

  // Site + date filtered, BEFORE any product filter. This is what populates
  // the product dropdown — so it only ever offers products they actually
  // bought in the chosen window, never the whole catalogue.
  const scopedOrders = useMemo(() => {
    const bySite = siteFilter === '__all__' ? orders : orders.filter(o => o.company_id === siteFilter)
    return bySite.filter(o => inRange(o.placed_at || o.created_at))
  }, [orders, siteFilter, from, to])

  const scopedOrdersLY = useMemo(() => {
    const bySite = siteFilter === '__all__' ? orders : orders.filter(o => o.company_id === siteFilter)
    return bySite.filter(o => inRangeLY(o.placed_at || o.created_at))
  }, [orders, siteFilter, from, to])

  // Products actually ordered in the current site+date scope. Coupon /
  // discount lines carry no product so they're excluded from the picker,
  // but still count towards the "all products" totals.
  const productOptions = useMemo(() => {
    const map = new Map()
    for (const o of scopedOrders) {
      for (const l of o.lines || []) {
        const sku = l.sku
        if (!sku || sku === 'COUPON') continue
        if (!map.has(sku)) map.set(sku, l.name || sku)
      }
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [scopedOrders])

  // Prune each order's lines to the selected product, then drop orders that
  // have nothing left. Filtering at LINE level matters: an invoice holding
  // Pet Packs AND dishwash tablets must contribute only its Pet Pack lines,
  // otherwise "Pet Pack spend" quietly absorbs the rest of the invoice.
  const applyProductFilter = (list) => {
    if (productFilter === '__all__') return list
    return list
      .map(o => ({ ...o, lines: (o.lines || []).filter(l => l.sku === productFilter) }))
      .filter(o => o.lines.length > 0)
  }

  const filteredOrders = useMemo(() => applyProductFilter(scopedOrders),   [scopedOrders, productFilter])
  const ordersLY       = useMemo(() => applyProductFilter(scopedOrdersLY), [scopedOrdersLY, productFilter])

  // If the chosen product drops out of scope (site or date changed and they
  // never bought it there), fall back to All rather than showing an
  // unexplained empty report.
  useEffect(() => {
    if (productFilter !== '__all__' && !productOptions.some(([sku]) => sku === productFilter)) {
      setProductFilter('__all__')
    }
  }, [productOptions, productFilter])

  // Aggregations
  const totals = useMemo(() => {
    let spend = 0, unitsKnown = 0, unitsUnknown = false, prodSet = new Set()
    // unit price = min price seen (unit vs case basis normalisation)
    const unitPriceBySku = new Map()
    for (const o of filteredOrders) for (const l of o.lines || []) {
      if (l.price && Number(l.price) > 0) {
        const k = l.sku || l.name
        const cur = unitPriceBySku.get(k)
        unitPriceBySku.set(k, cur == null ? Number(l.price) : Math.min(cur, Number(l.price)))
      }
    }
    for (const o of filteredOrders) for (const l of o.lines || []) {
      const sub = Number(l.subtotal || Number(l.qty || 0) * Number(l.price || 0))
      spend += sub
      const up = unitPriceBySku.get(l.sku || l.name)
      if (up) unitsKnown += sub / up; else unitsUnknown = true
      prodSet.add(l.sku || l.name)
    }
    return {
      spend, units: unitsKnown, unitsUnknown,
      orderCount: filteredOrders.length,
      productCount: prodSet.size,
      unitPriceBySku,
    }
  }, [filteredOrders])

  const totalsLY = useMemo(() => {
    let spend = 0
    for (const o of ordersLY) for (const l of o.lines || []) {
      spend += Number(l.subtotal || Number(l.qty || 0) * Number(l.price || 0))
    }
    return { spend, orderCount: ordersLY.length }
  }, [ordersLY])

  // YoY only makes sense when the prior-year window has meaningful data.
  // A trivial LY base (< £500 or 1–2 stray orders) creates absurd deltas
  // like +41000% that are honestly noise, not signal. Suppress in that case.
  // Suppress YoY unless the prior-year window has substantial data.
  // Needs both £ and volume — a client with only 3 stray prior-year orders
  // (like Woolacombe's first month of Sage history) shouldn't drive a
  // +4000% headline.
  const yoyMeaningful = totalsLY.spend >= 5000 && totalsLY.orderCount >= 10
  const yoyPct = yoyMeaningful ? ((totals.spend - totalsLY.spend) / totalsLY.spend) * 100 : null

  const activeSitesInPeriod = useMemo(() => {
    return new Set(filteredOrders.map(o => o.company_id).filter(Boolean)).size
  }, [filteredOrders])
  const avgSpendPerSite = activeSitesInPeriod > 0 ? totals.spend / activeSitesInPeriod : 0

  // Monthly trend
  const monthly = useMemo(() => {
    const map = new Map()
    for (const o of filteredOrders) {
      const k = monthKey(o.placed_at || o.created_at)
      if (!map.has(k)) map.set(k, 0)
      let orderSum = 0
      for (const l of o.lines || []) orderSum += Number(l.subtotal || Number(l.qty || 0) * Number(l.price || 0))
      map.set(k, map.get(k) + orderSum)
    }
    // Also fill missing months across range
    const start = new Date(from + 'T00:00:00')
    const end = new Date(to + 'T00:00:00')
    const cur = new Date(start.getFullYear(), start.getMonth(), 1)
    const filled = []
    while (cur <= end) {
      const k = cur.getFullYear() + '-' + String(cur.getMonth() + 1).padStart(2, '0')
      filled.push({ key: k, label: monthLabel(k), spend: map.get(k) || 0 })
      cur.setMonth(cur.getMonth() + 1)
    }
    return filled
  }, [filteredOrders, from, to])
  const maxMonthly = Math.max(1, ...monthly.map(m => m.spend))

  // Per-site
  const perSite = useMemo(() => {
    const map = new Map()
    // Seed with all companies so zero-spend sites still show
    for (const c of companies) map.set(c.id, { id: c.id, name: c.name, spend: 0, orders: 0, spendLY: 0, topCategory: null, catSpend: {} })
    for (const o of filteredOrders) {
      const id = o.company_id
      if (!map.has(id)) map.set(id, { id, name: o.company_name || 'Unknown', spend: 0, orders: 0, spendLY: 0, topCategory: null, catSpend: {} })
      const row = map.get(id)
      row.orders += 1
      for (const l of o.lines || []) {
        const sub = Number(l.subtotal || Number(l.qty || 0) * Number(l.price || 0))
        row.spend += sub
        const cat = l.category || 'Uncategorised'
        row.catSpend[cat] = (row.catSpend[cat] || 0) + sub
      }
    }
    for (const o of ordersLY) {
      const id = o.company_id
      if (!map.has(id)) map.set(id, { id, name: o.company_name || 'Unknown', spend: 0, orders: 0, spendLY: 0, topCategory: null, catSpend: {} })
      const row = map.get(id)
      for (const l of o.lines || []) row.spendLY += Number(l.subtotal || Number(l.qty || 0) * Number(l.price || 0))
    }
    for (const row of map.values()) {
      const entries = Object.entries(row.catSpend)
      if (entries.length) entries.sort((a, b) => b[1] - a[1])
      row.topCategory = entries[0]?.[0] || null
    }
    return [...map.values()].sort((a, b) => b.spend - a.spend)
  }, [filteredOrders, ordersLY, companies])
  const maxSite = Math.max(1, ...perSite.map(s => s.spend))

  // Per-category
  const perCategory = useMemo(() => {
    const map = new Map()
    for (const o of filteredOrders) for (const l of o.lines || []) {
      const cat = l.category || 'Uncategorised'
      if (!map.has(cat)) map.set(cat, 0)
      map.set(cat, map.get(cat) + Number(l.subtotal || Number(l.qty || 0) * Number(l.price || 0)))
    }
    return [...map.entries()].map(([name, spend]) => ({ name, spend })).sort((a, b) => b.spend - a.spend)
  }, [filteredOrders])
  const totalCatSpend = perCategory.reduce((s, c) => s + c.spend, 0) || 1

  // Top products by SKU
  const topProducts = useMemo(() => {
    const map = new Map()
    for (const o of filteredOrders) for (const l of o.lines || []) {
      const key = l.sku || l.name
      if (!map.has(key)) map.set(key, { sku: l.sku, name: l.name, spend: 0, orderIds: new Set(), unitsQty: 0, packSize: l.pack_size, category: l.category })
      const row = map.get(key)
      row.spend += Number(l.subtotal || Number(l.qty || 0) * Number(l.price || 0))
      const up = totals.unitPriceBySku.get(key)
      if (up) row.unitsQty = row.spend / up
      row.orderIds.add(o.id)
    }
    return [...map.values()]
      .map(r => ({ ...r, orderCount: r.orderIds.size }))
      .sort((a, b) => b.spend - a.spend)
      .slice(0, 10)
  }, [filteredOrders, totals.unitPriceBySku])

  const filteredSiteRows = perSite.filter(s => !siteSearch || s.name.toLowerCase().includes(siteSearch.toLowerCase()))

  return (
    <div className="space-y-4 max-w-6xl print:max-w-none">
      {tourOpen && <ReportiqTour mode={mode} brand={brand} onClose={() => setTourOpen(false)} />}

      {/* Take-a-tour button — always visible, non-intrusive */}
      <div className="flex items-center justify-end print:hidden -mb-2">
        <button
          onClick={() => setTourOpen(true)}
          className="inline-flex items-center gap-2 text-sm text-brand-dark hover:text-brand font-medium bg-white border border-brand/30 hover:border-brand rounded-full px-4 py-1.5 shadow-sm"
        >
          <PlayCircle className="w-4 h-4" /> Take a quick tour
        </button>
      </div>

      {/* Reporting-only banner */}
      {mode === 'reporting' && (
        <div className="rounded-2xl border border-brand/40 bg-brand-50 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide text-brand-dark font-semibold flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Reporting portal</div>
            <p className="text-sm text-gray-800 mt-1">
              You're on a <strong>reporting-only</strong> account showing your spend across your sites.
              Want your sites to place their own orders through here as well?
            </p>
          </div>
          <button
            onClick={onApplyForOrdering}
            className="btn-primary shrink-0"
          >
            Apply for order portal <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Controls */}
      <div className="card print:hidden">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-5 h-5 text-brand" />
          <h3 className="font-semibold text-gray-800">Spend overview</h3>
        </div>
        {/* Fields on one row; buttons on their own row so they can't squish
            the field labels underneath them at narrow widths. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <label>
            <span className="text-xs text-gray-500 block mb-1">Period</span>
            <select value={preset} onChange={e => applyPreset(e.target.value)} className="input-field">
              {PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">From</span>
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPreset('custom') }} className="input-field" />
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">To</span>
            <input type="date" value={to} onChange={e => { setTo(e.target.value); setPreset('custom') }} className="input-field" />
          </label>
          {siteOptions.length > 1 && (
            <label>
              <span className="text-xs text-gray-500 block mb-1">Site</span>
              <select value={siteFilter} onChange={e => setSiteFilter(e.target.value)} className="input-field">
                <option value="__all__">— All sites ({siteOptions.length}) —</option>
                {siteOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            </label>
          )}
          {productOptions.length > 1 && (
            <label className={siteOptions.length > 1 ? '' : 'lg:col-start-4'}>
              <span className="text-xs text-gray-500 block mb-1">Product</span>
              <select value={productFilter} onChange={e => setProductFilter(e.target.value)} className="input-field">
                <option value="__all__">— All products ({productOptions.length}) —</option>
                {productOptions.map(([sku, name]) => (
                  <option key={sku} value={sku}>{name}</option>
                ))}
              </select>
            </label>
          )}
        </div>

        {productFilter !== '__all__' && (
          <div className="mt-3 flex items-center gap-2 text-sm bg-brand-50 border border-brand/30 rounded-lg px-3 py-2">
            <Package className="w-4 h-4 text-brand-dark shrink-0" />
            <span className="text-gray-800">
              Showing <strong>{productOptions.find(([s]) => s === productFilter)?.[1] || productFilter}</strong> only —
              every figure below is for this product.
            </span>
            <button
              onClick={() => setProductFilter('__all__')}
              className="ml-auto text-xs font-medium text-brand-dark hover:underline whitespace-nowrap"
            >
              Clear
            </button>
          </div>
        )}
        <div data-tour="export-buttons" className="flex flex-wrap justify-end gap-2 mt-3">
          <button onClick={() => downloadCsv({ perSite, perCategory, topProducts, from, to, groupName: group?.name || 'Report' })} className="btn-secondary whitespace-nowrap">
            <Download className="w-4 h-4" /> Excel
          </button>
          <button onClick={() => window.print()} className="btn-primary whitespace-nowrap">
            <FileText className="w-4 h-4" /> Print / save PDF
          </button>
        </div>
      </div>

      {/* Executive summary — only visible in print output. Gives a formal
          "Annual Spend Report" opening page suitable for sharing with a team. */}
      <div className="hidden print:block mb-6 pb-6 border-b border-gray-300">
        <div className="text-xs uppercase tracking-wide text-brand-dark font-semibold">Annual Spend Report</div>
        <h1 className="text-3xl font-bold text-gray-900 mt-1">{group?.name || 'Your account'}</h1>
        <p className="text-sm text-gray-600 mt-1">Period: {fmtDate(from)} – {fmtDate(to)}</p>
        <div className="grid grid-cols-4 gap-4 mt-5">
          <div><div className="text-xs uppercase text-gray-500">Total spend</div><div className="text-2xl font-bold text-gray-900 tabular-nums">{fmtMoney(totals.spend)}</div></div>
          <div><div className="text-xs uppercase text-gray-500">Orders</div><div className="text-2xl font-bold text-gray-900 tabular-nums">{totals.orderCount}</div></div>
          <div><div className="text-xs uppercase text-gray-500">Active sites</div><div className="text-2xl font-bold text-gray-900 tabular-nums">{activeSitesInPeriod}</div></div>
          <div><div className="text-xs uppercase text-gray-500">Distinct SKUs</div><div className="text-2xl font-bold text-gray-900 tabular-nums">{totals.productCount}</div></div>
        </div>
        {yoyPct != null && (
          <p className="text-sm text-gray-700 mt-4 leading-relaxed">
            <strong>Executive summary.</strong> {group?.name || 'The account'} has spent <strong>{fmtMoney(totals.spend)}</strong> across {activeSitesInPeriod} sites in this period,
            {' '}<strong className={yoyPct >= 0 ? 'text-emerald-700' : 'text-red-700'}>{yoyPct >= 0 ? 'up' : 'down'} {Math.abs(yoyPct).toFixed(1)}%</strong> versus the equivalent prior period ({fmtMoney(totalsLY.spend)}).
            {perCategory[0] && <> The largest spend category is <strong>{perCategory[0].name}</strong> at {fmtMoney(perCategory[0].spend)} ({((perCategory[0].spend / totalCatSpend) * 100).toFixed(0)}% of total).</>}
            {perSite[0] && <> Top-spending site: <strong>{perSite[0].name}</strong> ({fmtMoney(perSite[0].spend)}).</>}
          </p>
        )}
      </div>

      {/* Headline tiles */}
      <div data-tour="stat-tiles" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="stat-tile min-w-0">
          <div className="section-label flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Total spend</div>
          <div className="text-3xl font-bold text-gray-900 tabular-nums leading-tight break-words">{fmtMoney(totals.spend)}</div>
          {yoyPct != null && (
            <div className={`text-xs mt-1 flex items-center gap-0.5 font-medium ${yoyPct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {yoyPct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {yoyPct >= 0 ? '+' : ''}{yoyPct.toFixed(1)}% vs last year
            </div>
          )}
        </div>
        <div className="stat-tile min-w-0">
          <div className="section-label flex items-center gap-1"><Building2 className="w-3 h-3" /> Active sites</div>
          <div className="text-3xl font-bold text-gray-900 tabular-nums leading-tight">{activeSitesInPeriod}</div>
          <div className="text-xs text-gray-500 mt-1">of {siteOptions.length} total</div>
        </div>
        <div className="stat-tile min-w-0">
          <div className="section-label flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Avg / site</div>
          <div className="text-3xl font-bold text-gray-900 tabular-nums leading-tight break-words">{fmtMoney(avgSpendPerSite)}</div>
          <div className="text-xs text-gray-500 mt-1">{totals.orderCount} orders in period</div>
        </div>
        <div className="stat-tile min-w-0">
          <div className="section-label flex items-center gap-1"><Package className="w-3 h-3" /> Products</div>
          <div className="text-3xl font-bold text-gray-900 tabular-nums leading-tight">{totals.productCount}</div>
          <div className="text-xs text-gray-500 mt-1">distinct SKUs</div>
        </div>
      </div>

      {/* Monthly trend */}
      <div data-tour="monthly-trend" className="card">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4 text-brand" />
          <h3 className="font-semibold text-gray-800">Monthly spend trend</h3>
        </div>
        {monthly.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No orders in this range.</p>
        ) : (
          <>
            <div className="flex items-end gap-1 sm:gap-2 h-48 border-b border-gray-200 pt-4">
              {monthly.map(m => {
                const pct = (m.spend / maxMonthly) * 100
                return (
                  <div key={m.key} className="flex-1 flex flex-col items-center min-w-0" title={`${monthLabel(m.key)}: ${fmtMoney(m.spend)}`}>
                    <div className="text-[10px] text-gray-600 tabular-nums mb-1 whitespace-nowrap">{m.spend > 0 ? fmtMoneyShort(m.spend) : ''}</div>
                    <div
                      className="w-full rounded-t bg-brand transition-all"
                      style={{ height: `${Math.max(2, pct)}%`, minHeight: '2px' }}
                    />
                  </div>
                )
              })}
            </div>
            <div className="flex gap-1 sm:gap-2 mt-2">
              {monthly.map((m, i) => {
                // On dense months (>8), only label every other tick to avoid overlap.
                const showLabel = monthly.length <= 8 || i % 2 === 0
                return (
                  <div key={m.key} className="flex-1 text-center text-[10px] text-gray-500 truncate min-w-0">
                    {showLabel ? m.label : ''}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Two-up: Spend by category + Top products */}
      <div data-tour="category-and-top" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-3">Spend by category</h3>
          {perCategory.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No categorised spend yet.</p>
          ) : (
            <div className="space-y-2">
              {perCategory.map(c => (
                <div key={c.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700">{c.name}</span>
                    <span className="font-medium text-gray-800 tabular-nums">{fmtMoney(c.spend)}<span className="text-gray-400 ml-2">({((c.spend / totalCatSpend) * 100).toFixed(0)}%)</span></span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-brand" style={{ width: `${(c.spend / totalCatSpend) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-3">Top products (by spend)</h3>
          {topProducts.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No products in this range.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                    <th className="text-left py-1">Product</th>
                    <th className="text-right py-1 px-3 w-16 whitespace-nowrap">Orders</th>
                    <th className="text-right py-1 px-3 w-24 whitespace-nowrap">Units</th>
                    <th className="text-right py-1 pl-3 w-28 whitespace-nowrap">Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((r, i) => (
                    <tr key={i} className="border-b border-gray-50">
                      <td className="py-2 pr-3">
                        <div className="font-medium text-gray-800 truncate">{r.name}</div>
                        {r.sku && <div className="text-xs text-gray-500 truncate">{r.sku}</div>}
                      </td>
                      <td className="text-right tabular-nums text-gray-700 whitespace-nowrap px-3">{r.orderCount}</td>
                      <td className="text-right tabular-nums text-gray-700 whitespace-nowrap px-3">{r.unitsQty ? fmtQty(r.unitsQty) : <span className="text-gray-400">—</span>}</td>
                      <td className="text-right tabular-nums font-medium text-gray-800 whitespace-nowrap pl-3">{fmtMoney(r.spend)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* By site — searchable table */}
      {siteOptions.length > 1 && (
        <div data-tour="by-site" className="card">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="font-semibold text-gray-800">By site</h3>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search sites…"
                value={siteSearch}
                onChange={e => setSiteSearch(e.target.value)}
                className="input-field pl-8 py-1.5 text-sm w-48"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                  <th className="text-left py-2">Site</th>
                  <th className="text-right py-2 w-24">Orders</th>
                  <th className="text-right py-2 w-28">Spend</th>
                  <th className="text-right py-2 w-28 hidden sm:table-cell">Last year</th>
                  <th className="text-right py-2 w-24 hidden sm:table-cell">Variance</th>
                  <th className="text-left py-2 w-40 hidden md:table-cell">Top category</th>
                </tr>
              </thead>
              <tbody>
                {filteredSiteRows.map(s => {
                  const variance = s.spendLY >= 5000 ? ((s.spend - s.spendLY) / s.spendLY) * 100 : null
                  return (
                    <tr key={s.id} className="border-b border-gray-50">
                      <td className="py-2 font-medium text-gray-800">
                        <button onClick={() => setSiteFilter(s.id)} className="hover:text-brand-dark text-left">{s.name}</button>
                      </td>
                      <td className="text-right tabular-nums text-gray-700">{s.orders}</td>
                      <td className="text-right tabular-nums font-medium text-gray-800">
                        <div className="flex items-center justify-end gap-2">
                          <div className="hidden sm:block h-1.5 bg-gray-100 rounded-full overflow-hidden" style={{ width: 60 }}>
                            <div className="h-full bg-brand rounded-full" style={{ width: `${(s.spend / maxSite) * 100}%` }} />
                          </div>
                          {fmtMoney(s.spend)}
                        </div>
                      </td>
                      <td className="text-right tabular-nums text-gray-500 hidden sm:table-cell">{s.spendLY > 0 ? fmtMoney(s.spendLY) : '—'}</td>
                      <td className={`text-right tabular-nums font-medium hidden sm:table-cell ${variance == null ? 'text-gray-400' : variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {variance == null ? '—' : (variance >= 0 ? '+' : '') + variance.toFixed(0) + '%'}
                      </td>
                      <td className="py-2 text-gray-600 hidden md:table-cell">{s.topCategory || '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="font-semibold text-gray-800">
                  <td className="pt-3">All sites</td>
                  <td className="pt-3 text-right tabular-nums">{totals.orderCount}</td>
                  <td className="pt-3 text-right tabular-nums">{fmtMoney(totals.spend)}</td>
                  <td className="pt-3 text-right tabular-nums text-gray-500 hidden sm:table-cell">{totalsLY.spend > 0 ? fmtMoney(totalsLY.spend) : '—'}</td>
                  <td className={`pt-3 text-right tabular-nums hidden sm:table-cell ${yoyPct == null ? 'text-gray-400' : yoyPct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {yoyPct == null ? '—' : (yoyPct >= 0 ? '+' : '') + yoyPct.toFixed(0) + '%'}
                  </td>
                  <td className="hidden md:table-cell"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 print:hidden">
        Period: {fmtDate(from)} — {fmtDate(to)}. Numbers derived from historical order data; unit counts assume the lowest recorded price is the true unit price.
      </p>
    </div>
  )
}

// Build a multi-section CSV (Excel opens it fine) covering the same three
// tables the on-screen dashboard shows: By site / By category / Top products.
// One file, blank rows separate the sections.
function downloadCsv({ perSite, perCategory, topProducts, from, to, groupName }) {
  const escape = (v) => {
    if (v == null) return ''
    const s = String(v)
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
  }
  const row = (cells) => cells.map(escape).join(',')
  const lines = []
  lines.push(row(['Annual Spend Report', groupName]))
  lines.push(row(['Period', from + ' to ' + to]))
  lines.push('')
  lines.push(row(['BY SITE']))
  lines.push(row(['Site', 'Orders', 'Spend (£)', 'Last year (£)', 'Variance %', 'Top category']))
  for (const s of perSite) {
    const variance = s.spendLY > 0 ? (((s.spend - s.spendLY) / s.spendLY) * 100).toFixed(1) : ''
    lines.push(row([s.name, s.orders, s.spend.toFixed(2), s.spendLY ? s.spendLY.toFixed(2) : '', variance, s.topCategory || '']))
  }
  lines.push('')
  lines.push(row(['BY CATEGORY']))
  lines.push(row(['Category', 'Spend (£)']))
  for (const c of perCategory) lines.push(row([c.name, c.spend.toFixed(2)]))
  lines.push('')
  lines.push(row(['TOP PRODUCTS']))
  lines.push(row(['SKU', 'Product', 'Orders', 'Units', 'Spend (£)']))
  for (const p of topProducts) lines.push(row([p.sku || '', p.name, p.orderCount, p.unitsQty ? p.unitsQty.toFixed(2) : '', p.spend.toFixed(2)]))
  const csv = '﻿' + lines.join('\n')  // BOM so Excel picks up UTF-8
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url
  a.download = `${(groupName || 'report').replace(/[^a-z0-9]+/gi, '-')}_${from}_to_${to}.csv`
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
